"use client";

import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  LoaderCircle,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "../lib/utils";

export type ToastType = "success" | "error" | "warning" | "loading" | "info";

export type ToastOptions = {
  title: string;
  description?: string;
  type?: ToastType;
  duration?: number | null;
  closable?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
};

type ToastItem = ToastOptions & {
  id: string;
  type: ToastType;
};

type ToastListener = (toasts: ToastItem[]) => void;

const DEFAULT_DURATION = 5000;

let sequence = 0;
let toasts: ToastItem[] = [];
const listeners = new Set<ToastListener>();
const timers = new Map<string, () => void>();

const emit = () => {
  for (const listener of listeners) {
    listener(toasts);
  }
};

const removeToast = (id: string) => {
  timers.get(id)?.();
  timers.delete(id);
  toasts = toasts.filter((toast) => toast.id !== id);
  emit();
};

const scheduleRemoval = (toast: ToastItem) => {
  if (toast.type === "loading" || toast.duration === null) {
    return;
  }

  const timeout = window.setTimeout(
    () => removeToast(toast.id),
    toast.duration ?? DEFAULT_DURATION,
  );
  timers.set(toast.id, () => window.clearTimeout(timeout));
};

export const toaster = {
  create(options: ToastOptions) {
    const toast = {
      ...options,
      id: `ui4-toast-${Date.now()}-${sequence++}`,
      type: options.type ?? "info",
    } satisfies ToastItem;

    const nextToasts = [toast, ...toasts].slice(0, 5);
    for (const removedToast of toasts.filter(
      (item) => !nextToasts.includes(item),
    )) {
      timers.get(removedToast.id)?.();
      timers.delete(removedToast.id);
    }
    toasts = nextToasts;
    emit();

    if (typeof window !== "undefined") {
      scheduleRemoval(toast);
    }

    return toast.id;
  },
  dismiss(id: string) {
    removeToast(id);
  },
  clear() {
    for (const clearTimer of timers.values()) {
      clearTimer();
    }
    timers.clear();
    toasts = [];
    emit();
  },
  subscribe(listener: ToastListener) {
    listeners.add(listener);
    listener(toasts);

    return () => {
      listeners.delete(listener);
    };
  },
};

export const Toaster = () => {
  const [visibleToasts, setVisibleToasts] = useState<ToastItem[]>([]);

  useEffect(() => toaster.subscribe(setVisibleToasts), []);

  if (visibleToasts.length === 0) {
    return null;
  }

  return (
    <section
      className="pointer-events-none fixed inset-x-0 top-4 z-[2500] flex flex-col items-center gap-2 px-4"
      aria-label="通知"
    >
      {visibleToasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} />
      ))}
    </section>
  );
};

const ToastCard = ({ toast }: { toast: ToastItem }) => {
  const tone = getToastTone(toast.type);
  const Icon = getToastIcon(toast.type);
  const isClosable = toast.closable ?? true;

  return (
    <div
      role={toast.type === "error" ? "alert" : "status"}
      className={cn(
        "pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg px-4 py-3 text-sm shadow-lg",
        tone.className,
      )}
    >
      <Icon
        className={cn(
          "mt-0.5 size-5 shrink-0",
          toast.type === "loading" && "animate-spin",
          tone.iconClassName,
        )}
      />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{toast.title}</p>
        {toast.description ? (
          <p className="mt-1 text-xs opacity-90">{toast.description}</p>
        ) : null}
        {toast.action ? (
          <button
            type="button"
            className="mt-1.5 inline-block rounded text-xs font-semibold underline underline-offset-2 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background/80"
            onClick={() => {
              toast.action?.onClick();
              toaster.dismiss(toast.id);
            }}
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>
      {isClosable ? (
        <button
          type="button"
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-md opacity-80 hover:bg-background/15 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background/80"
          aria-label="通知を閉じる"
          onClick={() => toaster.dismiss(toast.id)}
        >
          <X className="size-4" />
        </button>
      ) : null}
    </div>
  );
};

const getToastTone = (type: ToastType) => {
  switch (type) {
    case "success":
      return {
        className: "bg-success text-success-foreground",
        iconClassName: "text-success-foreground",
      };
    case "error":
      return {
        className: "bg-destructive text-destructive-foreground",
        iconClassName: "text-destructive-foreground",
      };
    case "warning":
      return {
        className: "bg-warning text-foreground",
        iconClassName: "text-warning-foreground",
      };
    default:
      return {
        className: "bg-primary text-primary-foreground",
        iconClassName: "text-primary-foreground",
      };
  }
};

const getToastIcon = (type: ToastType) => {
  switch (type) {
    case "success":
      return CheckCircle2;
    case "error":
      return AlertCircle;
    case "warning":
      return AlertTriangle;
    case "loading":
      return LoaderCircle;
    default:
      return CheckCircle2;
  }
};
